import { Injectable, OnDestroy } from '@angular/core';
import { AuthorizationServiceInterface } from './authorizationService.interface';
import { EventsHubService } from '../events-hub.service';
import { AuthenticationService } from './authentication.service';
import { ItemMenu, User } from '../../shared/models';
import TokenClient = google.accounts.oauth2.TokenClient;
import TokenResponse = google.accounts.oauth2.TokenResponse;
import ClientConfigError = google.accounts.oauth2.ClientConfigError;
import { environment } from '../../../environments/environment';
import {LocalStorageService} from '../../xform-compat';


@Injectable()
export class AuthorizationService implements AuthorizationServiceInterface, OnDestroy {

  private static SECS_BEFORE_REAUTH = 300;

  private currentUser: User;
  private filteredMenu: Array<ItemMenu>;
  private scope = [
    'https://www.googleapis.com/auth/calendar.readonly'
  ].join(' ');

  private tokenClient: TokenClient;
  private reAuthorizationTimer: ReturnType<typeof setTimeout>;

  constructor(
    private eventsHubService: EventsHubService,
    private authenticationService: AuthenticationService,
    private storage: LocalStorageService,
  ) {
    this.getCurrentUser();
    this.loggedInSubscription();
    this.googleAPIInit();
    if (this.currentUser) {
      this.requestAccessToken(this.currentUser.email);
    }
  }

  ngOnDestroy() {
    clearInterval(this.reAuthorizationTimer);
  }

  public isAdmin(): boolean {
    return this.currentUser.isAdmin;
  }

  public hasPermission(permissions: Array<string>): boolean {
    let isGranted: boolean;
    if (!permissions.length || this.isAdmin()) {
      isGranted = true;
    } else {
      isGranted = this.checkPermissionList(permissions);
    }
    return isGranted;
  }

  public requestAccessToken(email?: string) {
    let currentUserEmail = email;
    if (!email) {
      currentUserEmail = this.authenticationService.getUserEmail();
    }
    this.tokenClient.requestAccessToken({
      hint: currentUserEmail,
      prompt: ''
    });
  }

  private googleAPIInit() {
    // Initialize for google API calendar service
    this.tokenClient = google.accounts.oauth2.initTokenClient({
      client_id: environment.googleClientID,
      scope: this.scope,
      callback: (response) => this.handleTokenInit(response),
      error_callback: (err) => this.handleAuthError(err),
    });
  }

  private handleTokenInit(response: TokenResponse) {
    if (response.error) {
      return this.authenticationService.logout();
    }
    this.reAuthorizationTimer = setTimeout(() => {
      this.requestAccessToken();
    }, (+response.expires_in - AuthorizationService.SECS_BEFORE_REAUTH) * 1000);
  }

  private handleAuthError(err: ClientConfigError) {
    console.error(err);
    this.authenticationService.logout();
  }

  private checkPermissionList(requiredPermissions: Array<string>): boolean {
    let userPermissions: Array<string>;
    let hasAllPermissions = true;

    userPermissions = this.getAllUserPermissions();

    for (const permission of requiredPermissions) {
      if (!userPermissions.includes(permission)) {
        hasAllPermissions = false;
      }
    }

    return hasAllPermissions;
  }

  private getAllUserPermissions(): Array<string> {
    const userPermissions: Array<string> = [];
    return userPermissions;
  }

  public filterMenu(menu: Array<ItemMenu>): Array<any> {
    let filteredMenu: Array<ItemMenu> = [];

    if (!this.filteredMenu) {
      for (const item of menu) {
        if (this.hasPermission(item.permissionsNeeded)) {
          filteredMenu.push(item);
        }
      }
      this.filteredMenu = filteredMenu;
    }

    filteredMenu = this.filteredMenu;
    return filteredMenu;
  }

  private loggedInSubscription() {
    this.eventsHubService.isLoggedIn$
      .subscribe(
        (loggedIn: boolean) => {
          if (loggedIn) {
            this.getCurrentUser();
          }
        }
      );
  }

  private getCurrentUser() {
    this.currentUser = this.authenticationService.getLoggedUser();
  }

}
